import React, { useState, useEffect, useMemo } from "react";
import { Container, FlexContainer, Spacing } from "../Containers";
import { ColorVariables } from "../Colors";
import { CrossIcon, CheckIcon } from "../Images/icons";
import { Input } from "./Input";
import { baseLength } from "../Styles/globals";

const Validation = ({
  password,
  setValid,
  isConfirm = false,
  password2 = "",
}: {
  password2?: string;
  isConfirm?: boolean;
  password: string;
  setValid: (boolean) => void;
}) => {
  const [checkOne, setCheckOne] = useState(false);
  const [checkTwo, setCheckTwo] = useState(false);
  const [checkThree, setCheckThree] = useState(false);
  const [checkFour, setCheckFour] = useState(false);

  useEffect(() => {
    if (!password) {
      setCheckOne(false);
      setCheckTwo(false);
      setCheckThree(false);
      setCheckFour(false);
      return;
    }

    if (isConfirm) {
      setCheckFour(password === password2);
    }

    if (password.match(".*[a-z]+") && password.match(".*[A-Z]+")) {
      setCheckOne(true);
    } else {
      setCheckOne(false);
    }

    if (password.length >= 8) {
      setCheckTwo(true);
    } else {
      setCheckTwo(false);
    }

    if (password.match(".*[0-9]+") && password.match('.*[!@#$%^&*(),.?"`:{}|<>]+')) {
      setCheckThree(true);
    } else {
      setCheckThree(false);
    }
  }, [password, password2, isConfirm]);

  useEffect(() => {
    if (setValid) {
      if (isConfirm) return setValid(checkOne && checkFour);
      setValid(checkOne && checkTwo && checkThree);
    }
  }, [checkOne, checkTwo, checkThree, checkFour, setValid]);

  return (
    <Container
      className="fullWidth"
      maxWidth={baseLength * 150}
      mobileMaxWidth={baseLength * 82.5}
      paddingLeft={baseLength * 2}
      paddingRight={baseLength * 2}
      marginTop={baseLength}
    >
      {isConfirm && (
        <FlexContainer
          whiteSpace="nowrap"
          justifyContent="flex-start"
          alignItems="center"
          color={checkFour ? ColorVariables.mint : ColorVariables.cherry}
        >
          <span>
            {checkFour ? (
              <CheckIcon data-name="check4Pass" />
            ) : (
              <CrossIcon data-name="check4Fail" color={ColorVariables.cherry} />
            )}
          </span>
          <Spacing row space={baseLength * 3} mobileSpace={baseLength * 3} />
          <p className="small">Both passwords must match</p>
        </FlexContainer>
      )}
      {!isConfirm && (
        <>
          <FlexContainer
            whiteSpace="nowrap"
            justifyContent="flex-start"
            alignItems="center"
            color={checkOne ? ColorVariables.mint : ColorVariables.cherry}
          >
            <span>
              {checkOne ? (
                <CheckIcon data-name="check1Pass" />
              ) : (
                <CrossIcon data-name="check1Fail" color={ColorVariables.cherry} />
              )}
            </span>
            <Spacing row space={baseLength * 3} mobileSpace={baseLength * 3} />
            <p className="small">Uppercase and lowercase characters</p>
          </FlexContainer>

          <FlexContainer
            color={checkTwo ? ColorVariables.mint : ColorVariables.cherry}
            justifyContent="flex-start"
            alignItems="center"
          >
            <span>
              {checkTwo ? (
                <CheckIcon data-name="check2Pass" />
              ) : (
                <CrossIcon data-name="check2Fail" color={ColorVariables.cherry} />
              )}
            </span>
            <Spacing row space={baseLength * 3} mobileSpace={baseLength * 3} />
            <p className="small">At least 8 characters</p>
          </FlexContainer>

          <FlexContainer
            color={checkThree ? ColorVariables.mint : ColorVariables.cherry}
            justifyContent="flex-start"
            alignItems="center"
          >
            <span>
              {checkThree ? (
                <CheckIcon data-name="check3Pass" />
              ) : (
                <CrossIcon data-name="check3Fail" color={ColorVariables.cherry} />
              )}
            </span>
            <Spacing row space={baseLength * 3} mobileSpace={baseLength * 3} />
            <p className="small">Contains a symbol and a number</p>
          </FlexContainer>
        </>
      )}
    </Container>
  );
};

export type PasswordInputWithValidationProps = {
  autoComplete?: boolean;
  title?: string;
  name?: string;
  showLock?: boolean;
  value: string;
  onChange: ((...args: any) => any) | undefined;
  handleBlur?: ((...args: any) => any) | undefined;
  placeholder?: string;
  error?: boolean;
  caption?: string;
  onFocus?: ((...args: any) => any) | undefined;
  required?: boolean;
  handleKeyDown?: ((...args: any) => any) | undefined;
  className?: string;
  setValid: (boolean) => any;
  helperTitle?: string;
  helperBody?: JSX.Element;
  isConfirm?: boolean;
  password2?: string;
};

export const PasswordInputWithValidation = ({
  autoComplete = false,
  title = "",
  name = "",
  showLock = false,
  value = "",
  onChange,
  handleBlur,
  placeholder = "",
  error = false,
  caption = "",
  onFocus,
  required = false,
  handleKeyDown,
  className = "",
  setValid,
  helperTitle,
  helperBody,
  isConfirm = false,
  password2 = "",
}: PasswordInputWithValidationProps) => {
  const [active, setActive] = useState(false);
  const [showChecks, setShowChecks] = useState(false);
  const [internalIsValid, setInternalIsValid] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  const toggleHighlight = (newActiveValue = !active) => {
    setShowChecks(true); // Show checks once the user focuses on the input
    setActive(newActiveValue);
  };

  const handleOnChange = (val: string) => {
    if (onChange) {
      onChange(val);
    }
  };

  const handleOnFocus = () => {
    toggleHighlight(true);
    if (onFocus) {
      onFocus(true);
    }
  };

  const handleOnBlur = () => {
    toggleHighlight(false);
    if (handleBlur) {
      handleBlur();
    }
    setHasBlurred(true);
  };

  const handleValidPassword = (valid: boolean) => {
    setInternalIsValid(valid);
    setValid(valid);
  };

  const hasError = useMemo(() => {
    if (error) return true;
    if (!hasBlurred) return false;
    return !internalIsValid;
  }, [internalIsValid, value, error, hasBlurred]);

  return (
    <div>
      <Input
        autoComplete={autoComplete}
        title={title}
        name={name}
        type="password"
        showLock={showLock}
        helperTitle={helperTitle}
        helperBody={helperBody}
        value={value}
        placeholder={placeholder}
        error={hasError}
        caption={caption}
        required={required}
        handleKeyDown={handleKeyDown}
        className={className}
        onChange={handleOnChange}
        handleBlur={handleOnBlur}
        onFocus={handleOnFocus}
      />
      {showChecks && (
        <Validation isConfirm={isConfirm} password2={password2} setValid={handleValidPassword} password={value} />
      )}
    </div>
  );
};
